'use client'

import { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark' | 'system'

interface ThemeContextProps {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined)

const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'system'
  const saved = localStorage.getItem('theme') as Theme | null
  return saved || 'system'
}

export const ThemeProvider = ({
  children,
  primaryColor
}: {
  children: React.ReactNode
  primaryColor?: string
}) => {
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme())

  useEffect(() => {
    const root = document.documentElement
    // 다크모드 비활성화: 항상 라이트 테마만 적용
    // const isDark =
    //   theme === 'dark' ||
    //   (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)

    root.classList.remove('light', 'dark')
    root.classList.add('light')

    // 테마 로컬스토리지 저장
    if (theme === 'system') {
      localStorage.setItem(
        'theme',
        window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      )
    } else {
      localStorage.setItem('theme', theme)
    }

    // ✅ primaryColor가 있으면 CSS 변수로 반영
    if (primaryColor) {
      root.style.setProperty('--primary', primaryColor)
      localStorage.setItem('primary_color', primaryColor)
    }
  }, [theme, primaryColor])

  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
